/**
 * analytics.js — lead events for GA4 via GTM's dataLayer (spec task SEO-0.1).
 *
 * Page context (page_type, product, city, …) comes from pageContext.seoPage,
 * which gatsby-node.js attaches to every page it creates; gatsby-browser.js
 * hands it to setPageContext() on each route change. Nothing here is derived
 * from the URL string.
 *
 * Never pass name, email or phone values in params — buildLeadParams() only
 * copies the whitelisted keys below, so a stray PII field is dropped rather
 * than sent.
 *
 * CommonJS so the node test runner can load it without a transpile step.
 */

const LEAD_EVENTS = [
  `lead_form_submit`,
  `whatsapp_click`,
  `phone_click`,
  `booking_start`,
]

const PAGE_TYPES = [
  `office_hub`,
  `office_locality`,
  `building`,
  `office_listing`,
  `meeting_hub`,
  `meeting_listing`,
  `daypass_hub`,
  `blog`,
  `other`,
]

const PRODUCTS = [`office`, `meeting_room`, `day_pass`, `virtual_office`]

// Event params, in the order GA4 will show them. Everything else is dropped.
const PARAM_KEYS = [
  `page_type`,
  `product`,
  `city`,
  `locality`,
  `building_slug`,
  `space_id`,
  `form_id`,
]

let currentContext = {}

function setPageContext(seoPage) {
  currentContext = seoPage || {}
}

function getPageContext() {
  return currentContext
}

// Maps the camelCase pageContext.seoPage shape onto GA4's snake_case params.
function contextToParams(ctx = {}) {
  return {
    page_type: PAGE_TYPES.includes(ctx.pageType) ? ctx.pageType : `other`,
    product: PRODUCTS.includes(ctx.product) ? ctx.product : undefined,
    city: ctx.city,
    locality: ctx.locality,
    building_slug: ctx.buildingSlug,
    space_id: ctx.spaceId,
  }
}

/**
 * Builds the payload for one lead event: page context first, then explicit
 * params (e.g. a form that knows its own space_id) on top. Empty values are
 * omitted rather than sent as "" / undefined.
 */
function buildLeadParams(event, params = {}, ctx = currentContext) {
  if (!LEAD_EVENTS.includes(event)) {
    throw new Error(`trackLead: unknown event "${event}"`)
  }
  const merged = { ...contextToParams(ctx), ...params }
  const out = { event }
  PARAM_KEYS.forEach(key => {
    const value = merged[key]
    if (value !== undefined && value !== null && value !== ``) {
      out[key] = String(value)
    }
  })
  return out
}

function trackLead(event, params = {}) {
  if (typeof window === `undefined`) return null
  const payload = buildLeadParams(event, params)
  window.dataLayer = window.dataLayer || []
  window.dataLayer.push(payload)
  return payload
}

// Classifies a clicked href into the click-type lead event it represents.
function clickEventForHref(href) {
  if (!href) return null
  const h = String(href).trim().toLowerCase()
  if (h.startsWith(`tel:`)) return `phone_click`
  if (/^(https?:)?\/\/(api\.whatsapp\.com|wa\.me|web\.whatsapp\.com)\//.test(h)) {
    return `whatsapp_click`
  }
  if (h.startsWith(`whatsapp:`)) return `whatsapp_click`
  return null
}

module.exports = {
  LEAD_EVENTS,
  PAGE_TYPES,
  PRODUCTS,
  setPageContext,
  getPageContext,
  buildLeadParams,
  trackLead,
  clickEventForHref,
}
